import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ConceptChip } from './ConceptBadges'
import { useArtworkConcepts } from '../hooks/useKnowledge'

/**
 * What the knowledge graph says an artwork embodies, grouped by category. Managers only — the
 * endpoint is MANAGER-gated, so render this only for managers.
 */
export function ArtworkConceptsPanel({ artworkId }: { artworkId: number }) {
  const { data, isLoading } = useArtworkConcepts(artworkId)
  const concepts = data?.data ?? []

  const groups = new Map<string, typeof concepts>()
  for (const c of concepts) {
    const key = c.category ?? 'uncategorised'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(c)
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Knowledge graph</CardTitle>
        <CardDescription>
          Concepts linked to this artwork in the latest graph build ({concepts.length}).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <Skeleton className="h-16 w-full" />}
        {!isLoading && concepts.length === 0 && (
          <p className="text-sm text-slate-500">No concepts yet — this artwork has not been through enrichment.</p>
        )}
        {[...groups.entries()].map(([category, items]) => (
          <div key={category}>
            <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">{category}</div>
            <div className="flex flex-wrap gap-1.5">
              {items.map((c) => (
                <ConceptChip key={c.ucid} concept={c} />
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
